// @flow
import React from 'react'
import classNames from 'classnames'

// components
import { FieldLabel } from '../../Components'
import FormFields from '../../Components/_formFields/fields'

// types
import type { ProductContent } from '../Types/ProductContent'
import type { ExpandedSpecificationDTO, SpecificationDTO } from '../Types/Field'
import type { ArrayFieldFormTypes } from '../Types/ArrayFieldFormType'
import type { ResourcesType } from '../Types/Resources'

type TextFormProps = {
  addToArrayIndex?: number,
  arrayTypeBlockClassName?: string,
  arrayTypes?: ArrayFieldFormTypes,
  blockClassName: string,
  className?: string,
  content: ProductContent,
  contentBlockClassName: string,
  data: { [key: string]: any },
  debug: boolean,
  hiddenFields?: Array<string>,
  prefix: string,
  renderAfterField?: (fieldPath: string) => any,
  renderBeforeField?: (fieldPath: string) => any,
  renderWithoutBlockWrappers?: boolean,
  resources?: ResourcesType,
  specification: SpecificationDTO,
  tabKey?: string,
  tabs?: boolean,
  valueNames: { [key: string]: { [key: string]: string } },
}

type TextFormState = {
  rawSpecification: ExpandedSpecificationDTO,
  specification: any,
}

export default class TextForm extends React.Component<TextFormProps, TextFormState> {
  static defaultProps = {
    addToArrayIndex: 0,
    arrayTypeBlockClassName: '',
    arrayTypes: {},
    blockClassName: '',
    className: '',
    contentBlockClassName: 'card-body px-0 px-sm-3',
    hiddenFields: [],
    prefix: '',
    renderWithoutBlockWrappers: false,
    tabKey: '',
    tabs: false,
  }

  static getDerivedStateFromProps = (nextProps: TextFormProps, prevState: TextFormState) => {
    if (JSON.stringify(nextProps.specification) !== JSON.stringify(prevState.rawSpecification) && nextProps.specification &&
      nextProps.specification.fields) {
      return {
        rawSpecification: nextProps.specification,
        specification: {
          ...nextProps.specification,
          fields: nextProps.specification.fields.reduce((acc, field) => {
            acc[field.name] = field
            return acc
          }, {}),
          types: nextProps.specification.types.reduce((acc, type) => {
            acc[type.name] = type
            return acc
          }, {}),
        }
      }
    }
    return null
  }

  state = {
    rawSpecification: {},
    specification: {}
  }

  render() {
    const {
      arrayTypes, blockClassName, content, tabs, className, data, debug, valueNames, resources, prefix, contentBlockClassName,
      renderWithoutBlockWrappers, renderBeforeField, arrayTypeBlockClassName, addToArrayIndex, hiddenFields, renderAfterField
    } = this.props
    const { specification } = this.state

    if (!this.props.specification) {
      return null
    }

    return (
      content.fieldOrder.map<Object>((fieldName: string) => {
        if (specification.fields[fieldName] &&
          specification.fields[fieldName].enabled &&
          specification.fields[fieldName].domain._type === 'object' &&
          specification.fields[fieldName].domain.specification &&
          (!tabs || tabs && fieldName === this.props.tabKey)
        ) {
          const fieldSpecification = specification.fields[fieldName].domain.specification

          let blockProps = {
            addToArrayIndex,
            arrayTypeBlockClassName,
            arrayTypes: arrayTypes,
            className: classNames([className, 'w-100']),
            content: content.types[fieldSpecification.name],
            data: data[fieldName] || {},
            debug: debug,
            formData: data,
            hiddenFields: hiddenFields,
            key: `text-form-${fieldName}`,
            prefix: `${fieldName}-`,
            renderAfterField: renderAfterField,
            renderBeforeField: renderBeforeField,
            renderWithoutBlockWrappers: renderWithoutBlockWrappers,
            resources: resources,
            specification: fieldSpecification,
            textMode: true,
            valueNames: valueNames,
          }

          let Component = TextForm
          if (content.components && (content.components[fieldName] || content.components[fieldSpecification.name])) {
            let component = (content.components[fieldSpecification.name] || content.components[fieldName]).textForm
            if (component && FormFields[component]) {
              Component = FormFields[component]
              blockProps = { ...content.components[fieldName], ...blockProps }
              delete blockProps.name
            }
          }
          if (renderWithoutBlockWrappers) {
            return (
              <Component
                {...blockProps}
              />
            )
          }

          return (
            <div
              className={blockClassName}
              key={`card-${fieldName}`}
            >
              <div className={contentBlockClassName}>
                {tabs
                  ? null
                  : [
                    <h2 className='text-center' key={`text-form-h2-${fieldName}`}>
                      {content.fieldNames[fieldName]}
                    </h2>,
                    <hr className='d-none d-md-block w-100' key={`text-form-hr-${fieldName}`}/>
                  ]
                }
                <Component
                  {...blockProps}
                />
                {renderAfterField && typeof renderAfterField === 'function' ? renderAfterField(`${prefix}${fieldName}`) : null}
              </div>
            </div>
          )
        } else if (specification.fields[fieldName] &&
          specification.fields[fieldName].domain._type === 'array') {
          const fieldSpecification = specification.fields[fieldName].domain.specification
          const arrayLength = Array.isArray(data[fieldName]) ? data[fieldName].length : 0
          const arrayTitle = content.fieldNames[`${fieldName}ArrayTitle`] || `undefined: ${fieldName}-array-title`
          const arrayToRender = []
          for (let arrayIndex = 0; arrayIndex < arrayLength; arrayIndex++) {
            arrayToRender.push(
              <div className={arrayTypeBlockClassName} key={`array_${arrayIndex}`}>
                <div className='text-center' key={`text-form-array-${arrayIndex}-title`}>
                  <h2 id='insuredParties'>
                    {arrayTitle.replace('{value}', `${arrayIndex + 1 + addToArrayIndex}`)}
                  </h2>
                </div>
                <hr className='border-0 border-sm-top border-active-object' key={`text-form-array-${arrayIndex}-hr`}/>
                <TextForm
                  addToArrayIndex={addToArrayIndex}
                  arrayTypeBlockClassName={arrayTypeBlockClassName}
                  className={classNames([className, 'col-12'])}
                  content={content.types[fieldSpecification.name]}
                  data={data[fieldName][arrayIndex]}
                  debug={debug}
                  formData={data}
                  hiddenFields={hiddenFields}
                  prefix={`${fieldName}-`}
                  renderBeforeField={renderBeforeField}
                  resources={resources}
                  specification={fieldSpecification}
                  valueNames={valueNames}
                />
              </div>
            )
          }
          return arrayToRender
        } else if (
          specification.fields[fieldName] &&
          specification.fields[fieldName].enabled &&
          specification.fields[fieldName].domain._type !== 'object' &&
          specification.fields[fieldName].domain._type !== 'array' &&
          data
        ) {
          const type = specification.fields[fieldName].domain._type
          let showLabel = true
          let showLabelWrapper = true
          let additionalProps = {}
          let FieldComponent = FormFields.default
          let component = type
          let showAsText = false
          let hidden = false
          if (type === 'bool') {
            showLabel = false
            if (!data[fieldName]) {
              hidden = true
            }
          }
          if (content.components && content.components[fieldName]) {
            if (typeof content.components[fieldName].notForTextForm === 'undefined' || !content.components[fieldName].notForTextForm) {
              component = content.components[fieldName].name || type
            }
            if (typeof content.components[fieldName].hidden === 'boolean') {
              hidden = content.components[fieldName].hidden
            }
            if (content.components[fieldName].showAsText) {
              showAsText = true
            }
            if (typeof content.components[fieldName].showLabel === 'boolean') {
              showLabel = content.components[fieldName].showLabel
            }
            additionalProps = { ...additionalProps, ...content.components[fieldName] }
            delete additionalProps.name
            if (component === 'supplementaryInsuranceSelect') {
              showLabel = false
              showLabelWrapper = false
            }
          }
          if (FormFields[component]) {
            FieldComponent = FormFields[component]
            additionalProps.label = content.fieldNames[fieldName]
          }
          if (content.valueNames && content.valueNames[fieldName]) {
            additionalProps.valueNames = content.valueNames[fieldName]
          }
          if (valueNames && valueNames[fieldName]) {
            additionalProps.valueNames = { ...valueNames[fieldName], ...additionalProps.valueNames }
          }
          if (hiddenFields && hiddenFields.includes(`${prefix}${fieldName}`)) {
            hidden = true
          }
          if (showAsText) {
            return (
              <div className='row' key={fieldName}>
                {content.fieldNames[fieldName]}:&nbsp;<FieldComponent
                debug={debug}
                formData={data}
                id={fieldName}
                prefix={prefix}
                resources={resources}
                textMode
                value={data[fieldName]}
                {...specification.fields[fieldName]}
                {...additionalProps}
              />
              </div>
            )
          }
          return (
            !hidden && [
              renderBeforeField && typeof renderBeforeField === 'function' ? renderBeforeField(`${prefix}${fieldName}`) : null,
              <div className='row' key={fieldName}>
                {showLabelWrapper && <div className='col-sm-3 text-sm-right'>
                  {showLabel && <FieldLabel id={fieldName} label={content.fieldNames[fieldName]} wrapMode />}
                </div>}
                <div className={!showLabelWrapper ? 'col-sm-12' : 'col-sm-9'}>
                  <FieldComponent
                    debug={debug}
                    formData={data}
                    id={fieldName}
                    prefix={prefix}
                    resources={resources}
                    textMode
                    value={data[fieldName]}
                    {...specification.fields[fieldName]}
                    {...additionalProps}
                  />
                </div>
              </div>,
              renderAfterField && typeof renderAfterField === 'function' ? renderAfterField(`${prefix}${fieldName}`) : null,
            ])
        }
      })
    )
  }
}
